import { useRef } from "react";
import { AbortError, createFailure, FailureSeverity, validationFailed, validationSuccessful } from "sprint-common-web";

import { CreateEntityResponse } from "../../../../common/api/common/CreateEntityResponse";
import { ContentSource } from "../../../../common/types/ContentSource";
import { MovieContentSubtype } from "../../../../common/types/MovieContentSubtype";
import { CreateMovieRequest } from "../../api/CreateMovieRequest";
import { MovieReference } from "../../api/GetMoviesBySimilarTitleResponse";
import { useAssetsManageMovieApi } from "../../api/useAssetsManageMovieApi";
import { MovieItem } from "../MovieItem";
import { AddMovieFormInputs } from "./CreateMovieFormInputs";

export const useCreateMovieForm = (contentType: MovieContentSubtype) => {
    const { createMovie, getMoviesBySimilarTitle } = useAssetsManageMovieApi();

    const abortController = useRef<AbortController>(new AbortController());

    const createNewMovie = async (inputs: AddMovieFormInputs): Promise<MovieItem> => {
        const { title } = inputs;
        const createMovieRequest: CreateMovieRequest = {
            title,
            subtype: contentType
        };

        return createMovie(createMovieRequest).then((response: CreateEntityResponse) => {
            return {
                id: response.entityId,
                title,
                orphaned: true,
                includedSources: [ContentSource.Sprint],
                source: ContentSource.Sprint,
                versions: [],
                isModified: true,
                orphanedDetails: []
            };
        });
    };

    const getMoviesWithSimilarTitle = async (title: string): Promise<MovieReference[]> => {
        if (title) {
            const response = await getMoviesBySimilarTitle(
                { subtype: contentType, title },
                abortController.current.signal
            );

            return response.items;
        }

        return [];
    };

    const validateTitle = (input: string | undefined) => {
        abortController.current.abort();
        abortController.current = new AbortController();

        if (!input) {
            return validationSuccessful();
        }

        return getMoviesWithSimilarTitle(input)
            .then((movies: MovieReference[]) => {
                if (!movies || movies.length < 1) return validationSuccessful();

                const warningMessage = `Similar titles: ${movies.map((movie: MovieReference) => movie.title)}`;
                const failure = createFailure(warningMessage, "movie.SimilarTitle", FailureSeverity.Warning);

                return validationFailed([failure]);
            })
            .catch((error: Error) => {
                if (error instanceof AbortError) {
                    return validationSuccessful();
                }

                throw error;
            });
    };

    return {
        createNewMovie,
        validateTitle
    };
};
